<template>
  <div
    v-if="items && items.length"
    ref="section"
    class="container relative !left-0 !right-0 z-50 !mx-auto flex grid-cols-2 font-segoe"
  >
    <div
      ref="block"
      class="relative h-[672px] w-full overflow-hidden rounded-lg bg-white"
    >
      <div
        v-for="(item, i) in items"
        :key="i"
        class="absolute flex h-[672px] w-full overflow-hidden rounded-lg"
      >
        <div class="image-block relative flex h-full w-1/2 items-center">
          <CdnImg
            :src="handleImageSrc(item.image)"
            :alt="handleImageAlt(item.image)"
            loading="lazy"
            class="absolute inset-0 h-full w-full object-cover"
            :width="912"
            :height="672"
          />
        </div>

        <div class="text-block w-1/2 overflow-hidden">
          <div class="flex h-full items-center">
            <div class="bg-white py-4 pl-8 pr-6 lg:px-[120px] lg:py-16">
              <div
                v-if="item.title"
                class="title mb-4 text-xl uppercase leading-[1] text-slate md:text-3xl"
                v-html="item.title"
              />

              <ul
                v-if="item.list"
                class="flex flex-col gap-y-4 text-sm leading-[1.25rem] text-slate-lighten1 md:text-md md:leading-[1.8rem]"
              >
                <li
                  v-for="(listItem, li) in item.list"
                  :key="li"
                  class="relative flex min-h-[2.25rem] items-center pl-4 before:absolute before:bottom-auto before:left-0 before:top-auto before:block before:h-9 before:w-0.5 before:bg-[#00A7E1]"
                  v-html="listItem"
                />
              </ul>

              <div
                v-else-if="item.text"
                class="text-sm leading-[1.25rem] text-slate md:text-md md:leading-[1.8rem]"
                v-html="item.text"
              />

              <UiButton
                v-if="item.button && item.button.link"
                size="md"
                :to="item.button.link"
                :target="item.button.target"
                class="mt-8"
              >
                <span v-html="item.button.text" />
              </UiButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { CdnImg } from '@webplatform/cdn-image';
import { handleImageSrc, handleImageAlt } from '~/helpers';

interface Props {
  items?: any[];
}

withDefaults(defineProps<Props>(), {
  items: () => [],
});

const { $gsap, $ScrollTrigger } = useNuxtApp();
const section = ref();
const block = ref();

let context: ReturnType<typeof $gsap.context> | null = null;

onMounted(() => {
  if (!block.value) return;

  context = $gsap.context(() => {
    const contentChunks: HTMLElement[] = [...block.value.children];

    const tl = $gsap.timeline({
      scrollTrigger: {
        trigger: block.value,
        start: 'top top',
        end: '+=2500',
        scrub: 1,
        toggleActions: 'restart none none reverse',
      },
    });

    contentChunks.forEach((chunk, i) => {
      tl.fromTo(chunk, { zIndex: i === 0 ? 1 : 'auto' }, { zIndex: 2 });

      if (i !== 0) {
        tl.fromTo(
          chunk.querySelector('.text-block'),
          { yPercent: 10, autoAlpha: 0 },
          { yPercent: 0, autoAlpha: 1 },
        );

        tl.fromTo(
          chunk.querySelector('.image-block'),
          { autoAlpha: 0 },
          { autoAlpha: 1 },
          '<',
        );
      }

      if (i < contentChunks.length - 1) {
        tl.to(
          chunk.querySelector('.text-block'),
          { yPercent: -10, autoAlpha: 0 },
          '+=3',
        );
      }
    });

    $ScrollTrigger.create({
      trigger: section.value,
      start: 'center center',
      end: '+=3500',
      pin: true,
    });
  }, section.value);

  setTimeout(() => {
    $ScrollTrigger.refresh();
  }, 200);
});

onBeforeUnmount(() => {
  context?.revert();
  context = null;
});
</script>

<style lang="sass">
.pin-spacer
  min-width: 100% !important
  margin-left: auto !important
  margin-right: auto !important
</style>
